/**
 * Auction Mode Page.
 *
 * Two views: (1) event management list, (2) live auction selling screen.
 * The live screen is optimized for speed — big buttons, clear display,
 * and minimal clicks to sell or pass each lot.
 */

import { useState } from "react";
import {
  Gavel,
  Plus,
  Play,
  CheckCircle,
  XCircle,
  ArrowRight,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import type { PaginatedResponse } from "@/types/common";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface AuctionLot {
  id: string;
  event_id: string;
  asset_id: string;
  lot_number: number;
  starting_price: number;
  reserve_price: number | null;
  hammer_price: number | null;
  buyer_name: string | null;
  buyer_paddle: string | null;
  status: string;
  notes: string | null;
  sold_at: string | null;
  created_at: string;
}

interface AuctionEvent {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  event_date: string;
  status: string;
  current_lot_number: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  lots: AuctionLot[];
}

interface AuctionStats {
  total_lots: number;
  lots_sold: number;
  lots_passed: number;
  lots_pending: number;
  total_revenue: number;
  average_hammer_price: number;
  sell_through_rate: number;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function AuctionPage() {
  const [activeEventId, setActiveEventId] = useState<string | null>(null);

  if (activeEventId) {
    return (
      <LiveAuctionView
        eventId={activeEventId}
        onBack={() => setActiveEventId(null)}
      />
    );
  }

  return <EventListView onOpenEvent={setActiveEventId} />;
}

/** ======================== Event List View ======================== */

function EventListView({ onOpenEvent }: { onOpenEvent: (id: string) => void }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<PaginatedResponse<AuctionEvent>>({
    queryKey: ["auctions"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/auctions?page_size=50"))
        .data as PaginatedResponse<AuctionEvent>,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [desc, setDesc] = useState("");

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/auctions", {
          name,
          event_date: new Date(eventDate).toISOString(),
          description: desc || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auctions"] });
      setShowCreate(false);
      setName("");
      setDesc("");
      toast.success("Auction event created");
    },
    onError: () => toast.error("Failed to create auction event"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/auctions/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auctions"] });
      toast.success("Auction event deleted");
    },
    onError: () => toast.error("Failed to delete auction event"),
  });

  const statusBadge = (s: string) => {
    const v =
      s === "live"
        ? "danger"
        : s === "completed"
          ? "success"
          : s === "cancelled"
            ? "default"
            : "warning";
    return <Badge variant={v}>{s}</Badge>;
  };

  const events = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Auction Mode
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage auction events and sell lots at speed.
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> New Event
            </>
          )}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            <Input
              label="Event Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Spring Equipment Sale 2026"
            />
            <Input
              label="Event Date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
            />
            <Input
              label="Description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Optional notes"
            />
            <div className="sm:col-span-3 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create Event
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : events.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {events.map((ev) => (
            <Card
              key={ev.id}
              padding="sm"
              className="hover-lift cursor-pointer"
              onClick={() => onOpenEvent(ev.id)}
            >
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--fill-warning)] text-[var(--text-warning)] dark:bg-amber-900/50 dark:text-amber-300">
                  <Gavel className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {ev.name}
                    </span>
                    {statusBadge(ev.status)}
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                    {new Date(ev.event_date).toLocaleDateString()} ·{" "}
                    {ev.lots.length} lots
                  </p>
                </div>
                <div className="shrink-0 flex gap-2">
                  {ev.status === "scheduled" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(ev.id);
                      }}
                      className="rounded-[var(--radius-lg)] border border-[var(--text-danger)]/30 px-3 py-1 text-xs text-[var(--text-danger)] hover:bg-[var(--fill-danger)] dark:border-red-900 dark:hover:bg-red-900/10"
                    >
                      Delete
                    </button>
                  )}
                  <ArrowRight className="h-4 w-4 text-[var(--text-tertiary)]" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Gavel />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No auction events
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create an event to start selling.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

/** ======================== Live Auction View ======================== */

function LiveAuctionView({
  eventId,
  onBack,
}: {
  eventId: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();

  const { data: event, isLoading } = useQuery<AuctionEvent>({
    queryKey: ["auction", eventId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/auctions/${eventId}`)).data as AuctionEvent,
    refetchInterval: 3000,
  });

  const { data: stats } = useQuery<AuctionStats>({
    queryKey: ["auction-stats", eventId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/auctions/${eventId}/stats`))
        .data as AuctionStats,
    refetchInterval: 5000,
  });

  const startMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/auctions/${eventId}/start`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auction", eventId] });
      toast.success("Auction is live");
    },
    onError: () => toast.error("Failed to start auction"),
  });
  const completeMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/auctions/${eventId}/complete`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auction", eventId] });
      toast.success("Auction completed");
    },
    onError: () => toast.error("Failed to complete auction"),
  });

  // Lot management
  const [showAddLot, setShowAddLot] = useState(false);
  const [lotAssetId, setLotAssetId] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [lotStarting, setLotStarting] = useState("");
  const [lotReserve, setLotReserve] = useState("");

  const addLotMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(`/api/v1/auctions/${eventId}/lots`, {
          asset_id: lotAssetId,
          lot_number: parseInt(lotNumber),
          starting_price: parseFloat(lotStarting) || 0,
          reserve_price: lotReserve ? parseFloat(lotReserve) : undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auction", eventId] });
      setShowAddLot(false);
      setLotAssetId("");
      setLotNumber("");
      setLotStarting("");
      setLotReserve("");
      toast.success("Lot added", `Lot #${lotNumber} added to auction.`);
    },
    onError: () => toast.error("Failed to add lot"),
  });

  // Sell/pass state
  const [hammerPrice, setHammerPrice] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerPaddle, setBuyerPaddle] = useState("");

  const sellMutation = useMutation({
    mutationFn: async (lotId: string) =>
      (
        await apiClient.post(`/api/v1/auctions/${eventId}/lots/${lotId}/sell`, {
          hammer_price: parseFloat(hammerPrice),
          buyer_name: buyerName,
          buyer_paddle: buyerPaddle || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auction", eventId] });
      qc.invalidateQueries({ queryKey: ["auction-stats", eventId] });
      toast.success("Lot sold", `Sold to ${buyerName} for $${hammerPrice}`);
      setHammerPrice("");
      setBuyerName("");
      setBuyerPaddle("");
    },
    onError: () => toast.error("Failed to record sale"),
  });

  const passMutation = useMutation({
    mutationFn: async (lotId: string) =>
      (await apiClient.post(`/api/v1/auctions/${eventId}/lots/${lotId}/pass`))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auction", eventId] });
      qc.invalidateQueries({ queryKey: ["auction-stats", eventId] });
      toast.success("Lot passed");
    },
    onError: () => toast.error("Failed to pass lot"),
  });

  if (isLoading || !event) return <LoadingSpinner className="py-12" />;

  const activeLot = event.lots.find((l) => l.status === "active");
  const sortedLots = [...event.lots].sort(
    (a, b) => a.lot_number - b.lot_number,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="text-xs text-brand-600 hover:underline mb-1"
          >
            ← Back to Events
          </button>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            {event.name}
          </h2>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            {new Date(event.event_date).toLocaleDateString()} ·{" "}
            <Badge
              variant={
                event.status === "live"
                  ? "danger"
                  : event.status === "completed"
                    ? "success"
                    : "warning"
              }
            >
              {event.status}
            </Badge>
          </p>
        </div>
        <div className="flex gap-2">
          {event.status === "scheduled" && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowAddLot(!showAddLot)}
              >
                {showAddLot ? (
                  "Cancel"
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Add Lot
                  </>
                )}
              </Button>
              <Button
                size="sm"
                onClick={() => startMutation.mutate()}
                isLoading={startMutation.isPending}
                disabled={event.lots.length === 0}
              >
                <Play className="h-4 w-4" /> Go Live
              </Button>
            </>
          )}
          {event.status === "live" && !event.current_lot_number && (
            <Button
              size="sm"
              onClick={() => completeMutation.mutate()}
              isLoading={completeMutation.isPending}
            >
              <CheckCircle className="h-4 w-4" /> Complete Auction
            </Button>
          )}
        </div>
      </div>

      {/* Add Lot Form */}
      {showAddLot && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addLotMutation.mutate();
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-4"
          >
            <Input
              label="Asset ID"
              value={lotAssetId}
              onChange={(e) => setLotAssetId(e.target.value)}
              required
              placeholder="UUID of asset"
            />
            <Input
              label="Lot #"
              type="number"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              required
              placeholder="1"
            />
            <Input
              label="Starting Price"
              type="number"
              step="0.01"
              value={lotStarting}
              onChange={(e) => setLotStarting(e.target.value)}
              placeholder="0.00"
            />
            <Input
              label="Reserve Price"
              type="number"
              step="0.01"
              value={lotReserve}
              onChange={(e) => setLotReserve(e.target.value)}
              placeholder="Optional"
            />
            <div className="sm:col-span-4 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={addLotMutation.isPending}
              >
                Add Lot
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {[
            { label: "Total Lots", value: stats.total_lots },
            { label: "Sold", value: stats.lots_sold },
            { label: "Passed", value: stats.lots_passed },
            { label: "Pending", value: stats.lots_pending },
            { label: "Revenue", value: fmt(stats.total_revenue) },
            { label: "Avg Hammer", value: fmt(stats.average_hammer_price) },
            { label: "Sell-Through", value: `${stats.sell_through_rate}%` },
          ].map((s) => (
            <Card key={s.label} padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                {s.label}
              </p>
              <p className="text-lg font-bold text-[var(--text-primary)]">
                {s.value}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Active Lot — BIG CONTROLS */}
      {event.status === "live" && activeLot && (
        <Card className="border-2 border-brand-500">
          <div className="text-center mb-4">
            <p className="text-xs uppercase tracking-wider text-brand-600 font-semibold">
              Now Selling
            </p>
            <h3 className="text-4xl font-black text-[var(--text-primary)] mt-1">
              Lot #{activeLot.lot_number}
            </h3>
            <p className="text-lg text-[var(--text-secondary)] mt-1">
              Starting: {fmt(activeLot.starting_price)}
            </p>
            {activeLot.reserve_price && (
              <p className="text-xs text-[var(--text-tertiary)]">
                Reserve: {fmt(activeLot.reserve_price)}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
            <Input
              label="Hammer Price"
              type="number"
              step="0.01"
              value={hammerPrice}
              onChange={(e) => setHammerPrice(e.target.value)}
              placeholder="Final bid amount"
            />
            <Input
              label="Buyer Name"
              value={buyerName}
              onChange={(e) => setBuyerName(e.target.value)}
              placeholder="Winning bidder"
            />
            <Input
              label="Paddle #"
              value={buyerPaddle}
              onChange={(e) => setBuyerPaddle(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => sellMutation.mutate(activeLot.id)}
              disabled={!hammerPrice || !buyerName || sellMutation.isPending}
              className="flex items-center gap-2 rounded-[var(--radius-xl)] bg-emerald-600 px-8 py-4 text-lg font-bold text-white transition-all hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="h-6 w-6" /> SOLD
            </button>
            <button
              onClick={() => passMutation.mutate(activeLot.id)}
              disabled={passMutation.isPending}
              className="flex items-center gap-2 rounded-[var(--radius-xl)] border-2 border-[var(--text-danger)]/30 px-8 py-4 text-lg font-bold text-[var(--text-danger)] transition-all hover:bg-[var(--fill-danger)] dark:border-red-800 dark:hover:bg-red-900/10 disabled:opacity-50"
            >
              <XCircle className="h-6 w-6" /> PASS
            </button>
          </div>
        </Card>
      )}

      {/* Lot list */}
      <div className="space-y-2 stagger-children">
        {sortedLots.map((lot) => {
          const statusVariant =
            lot.status === "sold"
              ? "success"
              : lot.status === "active"
                ? "danger"
                : lot.status === "passed"
                  ? "default"
                  : "warning";
          return (
            <Card
              key={lot.id}
              padding="sm"
              className={
                lot.status === "active" ? "ring-2 ring-brand-500" : "hover-lift"
              }
            >
              <div className="flex items-center gap-4">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                    lot.status === "sold"
                      ? "bg-[var(--fill-success)] text-[var(--text-success)] dark:bg-emerald-900/30 dark:text-emerald-300"
                      : lot.status === "active"
                        ? "bg-[var(--fill-danger)] text-[var(--text-danger)] dark:bg-red-900/30 dark:text-red-300"
                        : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] dark:bg-gray-800"
                  }`}
                >
                  #{lot.lot_number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      Lot #{lot.lot_number}
                    </span>
                    <Badge variant={statusVariant}>{lot.status}</Badge>
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Start: {fmt(lot.starting_price)}
                    {lot.reserve_price &&
                      ` · Reserve: ${fmt(lot.reserve_price)}`}
                  </p>
                </div>
                {lot.status === "sold" && (
                  <div className="shrink-0 text-right">
                    <p className="text-lg font-bold text-[var(--text-success)]">
                      {fmt(lot.hammer_price!)}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      {lot.buyer_name}
                      {lot.buyer_paddle && ` (#${lot.buyer_paddle})`}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default AuctionPage;
